/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATH_DASHBOARD } from 'src/routes/paths';
import TaskSlideout from './TaskSlideout';
import TaskModalView from './TaskModalView';
import useTaskLayoutMode, { TASK_LAYOUT_MODES } from './useTaskLayoutMode';

// -----------------------------------------------
// TaskViewer (#179) — small parent-layer wrapper that decides which mode to
// render the active task in based on the user's persisted preference. Pages
// that previously rendered <TaskSlideout /> directly should render
// <TaskViewer /> with the same props instead.
//
// Modes:
//   sidebar — TaskSlideout (right drawer, 420px wide)
//   modal   — TaskModalView (centered MUI Dialog, ~720px wide)
//   full    — navigates to TaskDetailPage, closing the slideout/modal
//
// The layout switcher inside the slideout/modal header calls back via
// onChangeMode/onOpenFullView to drive these transitions without fully
// remounting the consumer page.
// -----------------------------------------------

export default function TaskViewer({
    open,
    onClose,
    taskId,
    practiceId,
    onTaskUpdated,
    onDeleteChecklist,
    taskList,
    onNavigateTask,
}) {
    const navigate = useNavigate();
    const { mode, setMode } = useTaskLayoutMode();

    // If the user picked "full" while the slideout was already open in a
    // previous session, do an immediate redirect when this viewer is opened.
    useEffect(() => {
        if (open && taskId && mode === TASK_LAYOUT_MODES.FULL) {
            navigate(PATH_DASHBOARD.task.tasks.detail(practiceId, taskId));
            // Reset to sidebar so closing the detail page and reopening a
            // task lands the user in the slideout, not in another redirect.
            // (Users who explicitly want full-page can open from the
            // switcher and they'll stay on the detail route.)
            setMode(TASK_LAYOUT_MODES.SIDEBAR);
            onClose?.();
        }
    }, [open, taskId, mode]);

    const handleOpenFullView = (id) => {
        if (!id) return;
        navigate(PATH_DASHBOARD.task.tasks.detail(practiceId, id));
        onClose?.();
    };

    const sharedProps = {
        open,
        onClose,
        taskId,
        practiceId,
        onTaskUpdated,
        onDeleteChecklist,
        onOpenFullView: handleOpenFullView,
        onChangeMode: setMode,
        taskList,
        onNavigateTask,
    };

    if (mode === TASK_LAYOUT_MODES.MODAL) {
        return <TaskModalView {...sharedProps} />;
    }
    return <TaskSlideout {...sharedProps} mode="sidebar" />;
}

TaskViewer.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    taskId: PropTypes.string,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onTaskUpdated: PropTypes.func,
    onDeleteChecklist: PropTypes.func,
    taskList: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object])),
    onNavigateTask: PropTypes.func,
};

TaskViewer.defaultProps = {
    taskId: null,
    onTaskUpdated: null,
    onDeleteChecklist: null,
    taskList: null,
    onNavigateTask: null,
};
